import { gql, useMutation, useQuery } from '@apollo/client';
import { Badge, Button, Dialog, Select } from '@radix-ui/themes';
import { ChevronDown, ChevronUp, CircleMinus, Loader, Plus } from 'lucide-react';
import { Accordion } from 'radix-ui';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { use, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { capitalizeName, sortByKey } from '../common';
import LocaleContext from '../hooks/LocaleContext';
import InviteExternalToJob from '../invite/InviteExternalToJob';
import MultiSelect from './MultiSelect';

const INVITE_TO_JOB_FROM_ORGANIZATION = gql`
  mutation InviteToJobFromOrganization($resourceId: String!, $invitees: [String!]) {
    InviteToJobFromOrganization(resourceId: $resourceId, invitees: $invitees)
  }
`;
const INVITE_GROUP_TO_JOB = gql`
  mutation grantGroupJobAccess($accessGroupId: String!, $jobId: String!, $accessLevel: JobAccessLevel) {
    grantGroupJobAccess(input: { accessGroupId: $accessGroupId, jobId: $jobId, accessLevel: $accessLevel })
  }
`;

const QUERY_CLIENT_MEMBERS = gql`
  query CLIENT_MEMBERS {
    members: viewer {
      client {
        members {
          nodes {
            id
            firstName
            lastName
            flag
          }
        }
      }
    }
  }
`;

const QUERY_CLIENT_ACCESSGROUPS = gql`
  query ACCESSGROUPS {
    accessGroups {
      id
      name
      description
      members {
        id
        firstName
        lastName
        email
        flag
      }
      jobs {
        job {
          id
          title
        }
      }
    }
    members: viewer {
      client {
        members {
          totalCount
          nodes {
            id
            firstName
            lastName
            flag
          }
        }
      }
    }
  }
`;

const REVOKE_ACCESS = gql`
  mutation REVOKE_ACCESS($userId: String!, $jobId: String!) {
    revokeUserJobAccess(input: { userId: $userId, jobId: $jobId })
  }
`;

const REVOKE_GROUP_ACCESS = gql`
  mutation REVOKE_GROUP_ACCESS($accessGroupId: String!, $jobId: String!) {
    revokeGroupJobAccess(input: { accessGroupId: $accessGroupId, jobId: $jobId })
  }
`;

const NamePill = ({ user }: { user: { id: string; firstName: string; lastName: string } }) => {
  const colors = ['green', 'purple', 'grey', 'red', 'yellow', 'blue'];

  const color = parseInt(user.id.substring(0, 8), 16) % colors.length;

  return <span className={'name-pill ' + colors[color]}>{user.firstName.slice(0, 1) + user.lastName.slice(0, 1)}</span>;
};

const AddCollaboratorToJob = (props: {
  users: { user: { id: string; firstName: string; lastName: string; email: string; client: string; flag: string } }[];
  groups: { accessGroup: { id: string; name: string; description: string; members: unknown[]; jobs: unknown[] } }[];
  refetching: () => void;
}) => {
  const { t } = useTranslation();
  const { userSettings } = use(LocaleContext);
  const { id } = useParams<{ id: string }>();

  const orgName = userSettings.company?.name ?? t('invite.accordion.organizationFallback');

  const [clientMembers, setClientMembers] = useState<
    {
      label: string;
      value: string;
      firstName: string;
      lastName: string;
      id: string;
    }[]
  >([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [accordionSelected, setAccordionSelected] = useState<string>('colleague');

  const [hover, setHover] = useState(false);

  /**
   * Accessgroup
   */
  const { data: clientAccessGroups, refetch } = useQuery<{ accessGroups: { id: string; name: string }[] }>(
    QUERY_CLIENT_ACCESSGROUPS,
  );
  const [accessGroups, setAccessGroups] = useState<{ label: string; value: string; id: string }[]>([]);
  const [addAccess, setAddAccess] = useState<{ accessGroupId: string }>();
  const [inviteGroupToJob] = useMutation(INVITE_GROUP_TO_JOB, {
    onCompleted() {
      props.refetching();
      refetch();
      setAddAccess(undefined);
    },
  });
  const [inviteToJobFromOrganization, { loading }] = useMutation(INVITE_TO_JOB_FROM_ORGANIZATION, {
    onCompleted() {
      props.refetching();
      refetch();
      onClear();
    },
  });
  useEffect(() => {
    if (clientAccessGroups && clientAccessGroups.accessGroups) {
      const arr = clientAccessGroups.accessGroups.map((x) => {
        return { label: x.name, value: x.id, id: x.id };
      });
      if (props.groups) {
        props.groups.map((group) => {
          const accessGroup = group.accessGroup;
          if (accessGroup) {
            const tempIndex = arr.findIndex((a) => a.id === accessGroup.id);
            if (tempIndex >= 0) arr.splice(tempIndex, 1);
          }
          return null;
        });
      }
      setAccessGroups(sortByKey(arr, (a) => a.label));
    }
  }, [clientAccessGroups, props.groups]);

  const onClear = () => {
    setSelected([]);
  };

  const handleChange = (value: string) => {
    setAddAccess({
      accessGroupId: value,
    });
  };

  const handleSubmit = () => {
    inviteGroupToJob({
      variables: {
        accessGroupId: addAccess?.accessGroupId,
        jobId: id,
        accessLevel: 'EDITOR',
      },
    });
  };

  const { data: clientMembersData } = useQuery<{
    members: { client: { members: { nodes: { id: string; firstName: string; lastName: string }[] } } };
  }>(QUERY_CLIENT_MEMBERS);

  useEffect(() => {
    if (clientMembersData) {
      const arr = clientMembersData.members.client.members.nodes.map((x) => {
        return {
          label: `${x.firstName} ${x.lastName}`,
          value: x.id,
          firstName: x.firstName,
          lastName: x.lastName,
          id: x.id,
        };
      });
      props.users.map((user) => {
        const theUser = user.user;
        if (theUser) {
          const tempIndex = arr.findIndex((a) => a.id === theUser.id);
          if (tempIndex >= 0) arr.splice(tempIndex, 1);
        }
        return null;
      });
      setClientMembers(sortByKey(arr, (a) => a.lastName));
    }
  }, [clientMembersData, props.users]);

  return (
    <Dialog.Root onOpenChange={(open) => !open && setAddAccess(undefined)}>
      <Dialog.Trigger>
        <span
          className="name-pill inline-flex! items-center justify-center"
          onMouseOver={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          <Plus size={hover ? 24 : 20} />
        </span>
      </Dialog.Trigger>
      <Dialog.Content size="4" aria-describedby={undefined}>
        <Dialog.Title>{t('invite.dialog.title')}</Dialog.Title>

        <Accordion.Root type="single" defaultValue="colleague" collapsible onValueChange={setAccordionSelected}>
          <Accordion.Item value="colleague">
            <Accordion.Header>
              <Accordion.Trigger className="font-bold w-full my-2">
                <span className="clickable flex justify-between">
                  <span>{t('invite.accordion.colleagueFromOrg', { orgName })}</span>
                  {accordionSelected === 'colleague' ? <ChevronUp /> : <ChevronDown />}
                </span>
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content className="accordion-content py-2">
              <MultiSelect
                className="w-full my-2"
                options={clientMembers}
                onChange={setSelected}
                placeholder={t('invite.colleague.pickerPlaceholder')}
              />

              <Button
                size="3"
                className="w-full!"
                disabled={selected.length === 0}
                loading={loading}
                onClick={() => {
                  if (id && selected && selected.length > 0) {
                    inviteToJobFromOrganization({
                      variables: {
                        resourceId: id,
                        invitees: selected,
                      },
                    })
                      .then((response) => {
                        if (response && response.data) console.log('inviteToJobFromOrganization response: ', response);
                      })
                      .catch((err) => {
                        console.error(err);
                      });
                  }
                }}
              >
                {t('invite.colleague.inviteButton')}
              </Button>
            </Accordion.Content>
          </Accordion.Item>
          <hr className="my-2 border-gray-200" />
          <Accordion.Item value="external">
            <Accordion.Header>
              <Accordion.Trigger className="font-bold w-full my-2">
                <span className="clickable flex justify-between">
                  <span>{t('invite.accordion.external')}</span>
                  {accordionSelected === 'external' ? <ChevronUp /> : <ChevronDown />}
                </span>
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content className="accordion-content py-2">
              <InviteExternalToJob refetching={props.refetching} />
            </Accordion.Content>
          </Accordion.Item>
          <hr className="my-2 border-gray-200" />
          <Accordion.Item value="group">
            <Accordion.Header>
              <Accordion.Trigger className="font-bold w-full my-2">
                <span className="clickable flex justify-between">
                  <span>{t('invite.accordion.group')}</span>
                  {accordionSelected === 'group' ? <ChevronUp /> : <ChevronDown />}
                </span>
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content className="accordion-content py-2">
              <AddAccessGroup
                accessGroups={accessGroups}
                handleChange={handleChange}
                handleSubmit={handleSubmit}
                value={addAccess?.accessGroupId}
                disabled={!addAccess?.accessGroupId}
              />
            </Accordion.Content>
          </Accordion.Item>
          <hr className="my-2 border-gray-200" />
        </Accordion.Root>

        {props.users && props.users.filter((user) => user.user !== null).length > 0 && (
          <div className="my-2 mx-0 px-0">
            <h1>{t('invite.sections.usersWithAccess')}</h1>

            {sortByKey(props.users, (u) => u.user?.lastName ?? '').map((user) => {
              const theUser = user.user;
              if (!theUser) return null;
              return (
                <div key={theUser.id} className="p-2 border-b">
                  <div className="float-left mr-2 ml-0 my-0 pr-4">
                    <NamePill user={theUser} />
                  </div>
                  <div className="flex justify-between ">
                    <h2 className="my-auto py-0">
                      {`${capitalizeName(theUser.firstName)} ${capitalizeName(theUser.lastName)}`}{' '}
                      {theUser.client ? null : (
                        <Badge variant="soft" color="amber" radius="large">
                          {t('invite.badges.external')}
                        </Badge>
                      )}
                      {theUser.flag === 'unconfirmed' ? (
                        <Badge variant="soft" color="amber" radius="large">
                          {t('invite.badges.notActivated')}
                        </Badge>
                      ) : null}
                      <br />
                      <small>{theUser.email}</small>
                    </h2>

                    <RevokeUserJobAccess
                      className="my-auto"
                      userId={theUser.id}
                      jobId={id}
                      refetching={props.refetching}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {props.groups && props.groups.length > 0 && (
          <div className="my-2 mx-0 px-0">
            <h1>{t('invite.sections.groupsWithAccess')}</h1>

            {props.groups.map((ag) => {
              const accessGroup = ag.accessGroup;
              return (
                <div key={accessGroup.id} className="p-2 border-b">
                  <div className="float-right my-2 ml-6 mr-0 ">
                    <RevokeUserJobAccess
                      className=""
                      accessGroupId={accessGroup.id}
                      jobId={id}
                      refetching={props.refetching}
                    />
                  </div>
                  <div className="flex align justify-between ">
                    <h2 className="my-0 py-0">{accessGroup.name}</h2>
                    <small className="text-gray-500">
                      {t('invite.group.memberCount', { count: accessGroup.members.length })}
                    </small>
                  </div>
                  <div className="flex justify-between">
                    <small>{accessGroup.description}</small>
                    <small className="text-gray-500">
                      {t('invite.group.jobCount', { count: accessGroup.jobs.length })}
                    </small>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex justify-end">
          <Dialog.Close>
            <Button>{t('invite.dialog.closeButton')}</Button>
          </Dialog.Close>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default AddCollaboratorToJob;

const RevokeUserJobAccess = ({
  userId,
  jobId,
  refetching,
  className,
  accessGroupId,
}: {
  userId?: string;
  jobId?: string;
  refetching: () => void;
  className: string;
  accessGroupId?: string;
}) => {
  const [loading, setLoading] = useState(false);
  const [hover, setHover] = useState(false);
  const [revokeUserJobAccess] = useMutation(REVOKE_ACCESS, {
    onCompleted(_data) {
      refetching();
    },
  });
  const [revokeGroupJobAccess] = useMutation(REVOKE_GROUP_ACCESS, {
    onCompleted(_data) {
      refetching();
    },
  });

  const handleRemove = () => {
    setLoading(true);
    if (userId) {
      revokeUserJobAccess({
        variables: {
          userId: userId,
          jobId: jobId,
        },
      }).finally(() => {
        setLoading(false);
        refetching();
      });
    } else if (accessGroupId) {
      revokeGroupJobAccess({
        variables: {
          accessGroupId: accessGroupId,
          jobId: jobId,
        },
      }).finally(() => {
        setLoading(false);
        refetching();
      });
    }
  };
  return (
    <span
      className={className}
      onClick={handleRemove}
      onMouseOver={() => {
        setHover(true);
      }}
      onMouseLeave={() => {
        setHover(false);
      }}
    >
      {loading ? (
        <Loader className="color dark-grey animate-spin" size={20} />
      ) : (
        <CircleMinus className={hover ? 'color red ' : 'color dark-grey'} size={20} />
      )}
    </span>
  );
};

const AddAccessGroup = ({
  accessGroups,
  handleChange,
  handleSubmit,
  value,
  disabled,
}: {
  accessGroups: { value: string; label: string }[];
  handleChange: (value: string) => void;
  handleSubmit: (e: ReactMouseEvent<HTMLElement, MouseEvent>) => void;
  value: string | undefined;
  disabled: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      <Select.Root size="3" value={value ?? ''} onValueChange={handleChange}>
        <Select.Trigger placeholder={t('invite.group.selectPlaceholder')} className="w-full!" />
        <Select.Content>
          {accessGroups.map((ag) => (
            <Select.Item key={ag.value} value={ag.value}>
              {ag.label}
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Root>
      <Button variant="outline" size="3" className="w-full!" onClick={handleSubmit} disabled={disabled}>
        {t('invite.group.addButton')}
      </Button>
    </div>
  );
};
